import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line,
} from "recharts";
import { TrendingUp, Trophy } from "lucide-react";

const formatMXN = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

export default function Reports() {
  const [days, setDays] = useState(14);
  const [series, setSeries] = useState([]);
  const [top, setTop] = useState([]);

  useEffect(() => {
    api.get("/reports/sales-by-day", { params: { days } }).then((r) =>
      setSeries(
        r.data.map((d) => ({
          ...d,
          label: new Date(d.date + "T00:00:00").toLocaleDateString("es-MX", { day: "numeric", month: "short" }),
        }))
      )
    );
  }, [days]);

  useEffect(() => {
    api.get("/reports/top-products", { params: { limit: 10 } }).then((r) => setTop(r.data));
  }, []);

  const total = series.reduce((s, x) => s + x.total, 0);
  const txns = series.reduce((s, x) => s + x.count, 0);
  const avg = txns > 0 ? total / txns : 0;

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="reports-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-clay font-semibold">Análisis</div>
          <h1 className="font-outfit text-4xl font-bold text-stone">Reportes</h1>
        </div>
        <div className="flex gap-2">
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`reports-range-${d}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                days === d ? "bg-stone text-bone" : "bg-white border border-warm-border text-stone hover:bg-sand"
              }`}
            >
              {d} días
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Stat label="Ingresos" value={formatMXN(total)} accent="clay" />
        <Stat label="Transacciones" value={txns} />
        <Stat label="Ticket promedio" value={formatMXN(avg)} />
      </div>

      <div className="bg-white rounded-xl border border-warm-border p-6 shadow-sm mb-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-clay" />
          <h3 className="font-outfit text-xl font-bold text-stone">Ventas por día</h3>
        </div>
        <div className="h-72" data-testid="sales-by-day-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" vertical={false} />
              <XAxis dataKey="label" stroke="#6A6863" fontSize={11} />
              <YAxis stroke="#6A6863" fontSize={11} />
              <Tooltip
                contentStyle={{ background: "white", border: "1px solid #E5E2DC", borderRadius: "8px" }}
                formatter={(v) => formatMXN(v)}
              />
              <Bar dataKey="total" fill="#C85A40" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-warm-border p-6 shadow-sm">
          <h3 className="font-outfit text-xl font-bold text-stone mb-4">Tendencia de transacciones</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" vertical={false} />
                <XAxis dataKey="label" stroke="#6A6863" fontSize={11} />
                <YAxis stroke="#6A6863" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "white", border: "1px solid #E5E2DC", borderRadius: "8px" }}
                />
                <Line type="monotone" dataKey="count" stroke="#7C9082" strokeWidth={3} dot={{ r: 4, fill: "#7C9082" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-warm-border p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-4 h-4 text-clay" />
            <h3 className="font-outfit text-xl font-bold text-stone">Top productos</h3>
          </div>
          <div className="space-y-2" data-testid="top-products-list">
            {top.length === 0 && <div className="text-sm text-stone/50">Sin datos.</div>}
            {top.map((p, i) => (
              <div
                key={p.product_id}
                className="flex items-center gap-3 p-3 bg-sand/50 rounded-lg border border-warm-border"
              >
                <div className="w-7 h-7 rounded-md bg-clay/10 text-clay flex items-center justify-center font-outfit font-bold text-sm">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-stone truncate">{p.name}</div>
                  <div className="text-xs text-stone/50">{p.qty} unidades vendidas</div>
                </div>
                <div className="font-outfit font-semibold text-stone text-sm">{formatMXN(p.revenue)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`p-6 rounded-xl border border-warm-border shadow-sm ${accent === "clay" ? "bg-stone text-bone" : "bg-white"}`}>
      <div className={`text-[11px] uppercase tracking-[0.2em] font-semibold ${accent === "clay" ? "text-bone/60" : "text-stone/50"}`}>
        {label}
      </div>
      <div className={`font-outfit text-3xl font-bold mt-2 ${accent === "clay" ? "" : "text-stone"}`}>
        {value}
      </div>
    </div>
  );
}
